import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import {
  useProductRecommendationStatus,
  type ProductRecommendationShopMeta,
} from "@/api/product-recommendations/use-product-recommendation-status";
import { useProductRecommendationSources } from "@/api/product-recommendations/use-product-recommendation-sources";
import { useProductRecommendationsTest } from "@/api/product-recommendations/use-product-recommendations-test";
import { useTriggerScoresComputation } from "@/api/product-recommendations/use-trigger-scores-computation";
import { useViewerIsSuperAdmin } from "@/lib/rights/use-viewer-is-baback-team";
import { Badge } from "@babackapp/ui/components/badge";
import { Button } from "@babackapp/ui/components/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@babackapp/ui/components/card";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@babackapp/ui/components/command";
import { Input } from "@babackapp/ui/components/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@babackapp/ui/components/popover";
import { Skeleton } from "@babackapp/ui/components/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@babackapp/ui/components/table";
import { Tabs, TabsList, TabsTrigger } from "@babackapp/ui/components/tabs";
import { cn } from "@babackapp/ui/lib/utils";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  ChevronsUpDown,
  Loader2,
  RefreshCw,
  Search,
} from "lucide-react";
import { useState } from "react";
import { Link, Navigate } from "react-router-dom";

type PairType = "same_order" | "customer_history";

function formatTs(ts: number | null): string {
  if (!ts) return "—";
  return format(new Date(ts), "dd/MM/yyyy HH:mm", { locale: fr });
}

function StatusBadge({ meta }: { meta: ProductRecommendationShopMeta }) {
  const now = Date.now();
  const dayMs = 24 * 60 * 60 * 1000;
  const isStale =
    !meta.lastOrderPairConsolidation ||
    now - meta.lastOrderPairConsolidation > dayMs;

  if (!meta.lastOrderPairConsolidation) {
    return (
      <Badge variant="outline" className="gap-1 text-muted-foreground">
        <Clock className="w-3 h-3" />
        En attente
      </Badge>
    );
  }
  if (isStale) {
    return (
      <Badge
        variant="outline"
        className="gap-1 text-yellow-600 border-yellow-300"
      >
        <AlertCircle className="w-3 h-3" />
        Stale
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="gap-1 text-green-600 border-green-300">
      <CheckCircle2 className="w-3 h-3" />À jour
    </Badge>
  );
}

function ShopsStatusTable() {
  const {
    data: statusList,
    isLoading,
    isError,
    refetch,
  } = useProductRecommendationStatus();
  const triggerScores = useTriggerScoresComputation();

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <p className="text-sm text-destructive py-4">
        Erreur lors du chargement des données.{" "}
        <button className="underline" onClick={() => refetch()}>
          Réessayer
        </button>
      </p>
    );
  }

  if (!statusList || statusList.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-4">
        Aucun shop activé pour le moment. Passe{" "}
        <code className="font-mono text-xs bg-muted px-1 rounded">
          plan.productRecommendation = true
        </code>{" "}
        sur un shop et active les flags config.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button size="sm" variant="outline" onClick={() => refetch()}>
          <RefreshCw className="w-3 h-3 mr-1" />
          Rafraîchir
        </Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Shop ID</TableHead>
            <TableHead>Statut</TableHead>
            <TableHead>Paires orders</TableHead>
            <TableHead>Dernière MAJ paires</TableHead>
            <TableHead>Dernière MAJ scores</TableHead>
            <TableHead>Reco calculées</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {statusList.map((meta) => (
            <TableRow key={meta.shopId}>
              <TableCell className="font-mono text-xs">
                <Link
                  to={`/shops/${meta.shopId}/analytics/product-recommendations`}
                  className="hover:underline"
                >
                  {meta.shopId}
                </Link>
              </TableCell>
              <TableCell>
                <StatusBadge meta={meta} />
              </TableCell>
              <TableCell className="text-sm">
                {meta.totalOrderPairs?.toLocaleString("fr-FR") ?? "—"}
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {formatTs(meta.lastOrderPairConsolidation)}
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {formatTs(meta.lastScoreComputation)}
              </TableCell>
              <TableCell className="text-sm">
                {meta.totalRecommendations?.toLocaleString("fr-FR") ?? "—"}
              </TableCell>
              <TableCell>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={
                    triggerScores.isPending &&
                    triggerScores.variables === meta.shopId
                  }
                  onClick={() => triggerScores.mutate(meta.shopId)}
                >
                  {triggerScores.isPending &&
                  triggerScores.variables === meta.shopId ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    "Recalculer scores"
                  )}
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function RecommendationTester({ defaultShopId }: { defaultShopId: string }) {
  const [shopId, setShopId] = useState(defaultShopId);
  const [sourceId, setSourceId] = useState("");
  const [submittedSourceId, setSubmittedSourceId] = useState("");
  const [pairType, setPairType] = useState<PairType>("same_order");
  const [comboOpen, setComboOpen] = useState(false);
  const [showManualInput, setShowManualInput] = useState(false);

  const { data: sources, isLoading: isSourcesLoading } =
    useProductRecommendationSources({ shopId, pairType });

  const { data, isLoading, isFetching } = useProductRecommendationsTest(
    { shopId, sourceId: submittedSourceId, pairType, limit: 15 },
    !!submittedSourceId && !!shopId,
  );

  const handleSearch = () => {
    if (sourceId.trim()) setSubmittedSourceId(sourceId.trim());
  };

  const selectedSource = sources?.find((s) => s.sourceId === submittedSourceId);
  const comboLabel = selectedSource
    ? `${selectedSource.productTitle ?? selectedSource.sourceId} (${selectedSource.count})`
    : submittedSourceId
      ? submittedSourceId
      : "Chercher un produit...";

  return (
    <div className="space-y-4">
      {/* Inputs */}
      <div className="flex gap-2 flex-wrap items-end">
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground font-medium">Shop ID</p>
          <Input
            className="w-64 font-mono text-xs"
            placeholder="shopId"
            value={shopId}
            onChange={(e) => {
              setShopId(e.target.value);
              setSubmittedSourceId("");
            }}
          />
        </div>

        <div className="space-y-1">
          <p className="text-xs text-muted-foreground font-medium">
            Produit source
          </p>
          {isSourcesLoading ? (
            <Skeleton className="h-9 w-80" />
          ) : (
            <Popover open={comboOpen} onOpenChange={setComboOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={comboOpen}
                  className="w-80 justify-between font-normal text-xs"
                >
                  <span className="truncate">{comboLabel}</span>
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-0" align="start">
                <Command>
                  <CommandInput
                    placeholder="Chercher un produit..."
                    className="text-xs"
                  />
                  <CommandEmpty className="py-3 text-center text-xs text-muted-foreground">
                    Aucun produit trouvé.
                  </CommandEmpty>
                  <CommandList className="max-h-60 overflow-y-auto">
                    <CommandGroup
                      heading={
                        sources && sources.length > 0
                          ? `${sources.length} produits — recherche pour filtrer`
                          : undefined
                      }
                    >
                      {(sources ?? []).map((source) => {
                        const title = source.productTitle ?? source.sourceId;
                        const searchValue = title;
                        const isSelected =
                          submittedSourceId === source.sourceId;
                        return (
                          <CommandItem
                            key={source.sourceId}
                            value={searchValue}
                            onSelect={() => {
                              setSourceId(source.sourceId);
                              setSubmittedSourceId(source.sourceId);
                              setComboOpen(false);
                            }}
                            className={cn(
                              "text-xs gap-2",
                              isSelected && "font-medium",
                            )}
                          >
                            {source.imageUrl ? (
                              <img
                                src={source.imageUrl}
                                alt=""
                                className="w-7 h-7 rounded object-cover shrink-0"
                              />
                            ) : (
                              <div className="w-7 h-7 rounded bg-muted shrink-0" />
                            )}
                            <span className="flex-1 min-w-0">
                              <span className="block truncate">{title}</span>
                              <span className="block text-muted-foreground">
                                {source.count} recos
                              </span>
                            </span>
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          )}
          <button
            type="button"
            onClick={() => setShowManualInput((v) => !v)}
            className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground transition-colors"
          >
            {showManualInput
              ? "Masquer la saisie manuelle"
              : "Entrer un ID manuellement"}
          </button>
        </div>

        {showManualInput && (
          <div className="flex gap-2 items-end">
            <Input
              className="w-80 font-mono text-xs"
              placeholder="babackCatalogId ou productId source"
              value={sourceId}
              onChange={(e) => setSourceId(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            />
            <Button
              onClick={handleSearch}
              size="sm"
              disabled={!sourceId.trim()}
            >
              <Search className="w-4 h-4 mr-1" />
              Chercher
            </Button>
          </div>
        )}
      </div>

      {/* Filtres */}
      <div className="flex gap-3">
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground font-medium">Type</p>
          <Tabs
            value={pairType}
            onValueChange={(v) => {
              setPairType(v as PairType);
              setSubmittedSourceId("");
            }}
          >
            <TabsList className="h-8">
              <TabsTrigger value="same_order" className="text-xs px-3 h-6">
                Same order
              </TabsTrigger>
              <TabsTrigger
                value="customer_history"
                className="text-xs px-3 h-6"
              >
                Historique client
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* Résultats */}
      {submittedSourceId && (
        <div className="relative">
          {isFetching && (
            <div className="absolute inset-0 flex items-center justify-center bg-background/60 z-10 rounded">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          )}

          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-9 w-full" />
              ))}
            </div>
          ) : !data || data.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              Aucune recommendation trouvée pour cet ID.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Produit recommandé</TableHead>
                  <TableHead className="text-right">Lift ↑</TableHead>
                  <TableHead className="text-right">Confidence</TableHead>
                  <TableHead className="text-right">Jaccard</TableHead>
                  <TableHead className="text-right">Co-occur.</TableHead>
                  <TableHead className="text-right">Ranking</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map((rec) => (
                  <TableRow key={rec.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {rec.targetImageUrl ? (
                          <img
                            src={rec.targetImageUrl}
                            alt=""
                            className="w-8 h-8 rounded object-cover shrink-0"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded bg-muted shrink-0" />
                        )}
                        <div className="min-w-0">
                          {rec.targetProductTitle ? (
                            <p className="text-xs font-medium truncate max-w-[220px]">
                              {rec.targetProductTitle}
                            </p>
                          ) : (
                            <p className="font-mono text-xs text-muted-foreground truncate max-w-[220px]">
                              {rec.targetId}
                            </p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-right text-sm font-medium">
                      {rec.lift.toFixed(2)}
                    </TableCell>
                    <TableCell className="text-right text-sm">
                      {(rec.confidence * 100).toFixed(1)}%
                    </TableCell>
                    <TableCell className="text-right text-sm">
                      {(rec.jaccard * 100).toFixed(1)}%
                    </TableCell>
                    <TableCell className="text-right text-sm text-muted-foreground">
                      {rec.coOccurrenceCount.toLocaleString("fr-FR")}
                    </TableCell>
                    <TableCell className="text-right text-sm text-muted-foreground">
                      {rec.rankingScore.toFixed(3)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      )}
    </div>
  );
}

export default function ProductRecommendationsPage() {
  const { data: shop } = useCurrentShopSuspense();
  const isSuperAdmin = useViewerIsSuperAdmin();

  if (!isSuperAdmin) {
    return <Navigate to={`/shops/${shop.id}`} replace />;
  }

  return (
    <div className="flex items-center justify-center">
      <div className="grid grid-cols-4 gap-4 w-full max-w-[1600px] lg:px-16 lg:py-6 px-6 py-4">
        {/* Header */}
        <div className="col-span-4 mb-2 space-y-1">
          <div className="flex items-center gap-2">
            <h2 className="text-2xl leading-6 font-semibold text-foreground">
              Recommendations produit
            </h2>
            <Badge variant="outline" className="text-xs font-normal">
              @baback.co interne
            </Badge>
          </div>
          <p className="text-sm leading-relaxed text-muted-foreground max-w-[640px]">
            Outil de test pour l'algorithme de recommendations basé sur les
            co-occurrences de produits dans les commandes (Lift + Jaccard).
          </p>
        </div>

        {/* Status des shops */}
        <div className="col-span-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-medium">
                État de la consolidation — tous les shops activés
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ShopsStatusTable />
            </CardContent>
          </Card>
        </div>

        {/* Testeur de recommendations */}
        <div className="col-span-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-medium">
                Tester les recommendations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <RecommendationTester defaultShopId={shop.id} />
            </CardContent>
          </Card>
        </div>

        {/* Légende des métriques */}
        <div className="col-span-4">
          <Card className="bg-muted/40">
            <CardContent className="pt-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs text-muted-foreground">
                <div>
                  <p className="font-medium text-foreground mb-1">Lift</p>
                  <p>
                    Corrélation vs hasard. {">"} 1 = association positive. {">"}{" "}
                    3 = forte association.
                  </p>
                </div>
                <div>
                  <p className="font-medium text-foreground mb-1">Confidence</p>
                  <p>
                    P(B|A) — % des orders contenant A qui contiennent aussi B.
                  </p>
                </div>
                <div>
                  <p className="font-medium text-foreground mb-1">Jaccard</p>
                  <p>
                    |A∩B| / |A∪B| — balance la popularité. Filtre les produits
                    "universels".
                  </p>
                </div>
                <div>
                  <p className="font-medium text-foreground mb-1">Ranking</p>
                  <p>
                    Lift × log(1 + co-occur.) — score de tri final combinant
                    force et fréquence.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
